
// Navigation and UI Data Types
export interface NavItem {
    label: string;
    active: boolean;
    available: boolean;
  }
  
  export interface Tab {
    id: string;
    label: string;
    icon: string;
    active: boolean;
  }
  
  // Jurisdiction and Location Data Types
  export interface Jurisdiction {
    name: string;
    state?: string;
    displayName?: string;
  }
  
  export interface PeerJurisdiction extends Jurisdiction {
    id?: string;
    isSelected?: boolean;
  }
  
  // Chart and Benchmark Data Types
  export interface ChartDataPoint {
    name: string;
    value: string;
    percentage?: number;
    color: string;
  }
  
  export interface ChartMetadata {
    category: string;
    title: string;
    subtitle: string;
    mainValue: string;
    mainLocation: string;
    rank: string;
    xAxisLabel: string;
    yAxisLabels: string[];
  }
  
  export interface ChartCard extends ChartMetadata {
    data: ChartDataPoint[];
  }
  
  // Benchmark Categories
  export type BenchmarkCategory = 
    | 'POPULATION'
    | 'PERMITTING' 
    | 'HOUSING STOCK'
    | 'AFFORDABILITY'
    | 'AVAILABILITY';
  
  // Featured Benchmarks
  export interface FeaturedBenchmark {
    text: string;
    icon: string;
  }
  
  // Autocomplete and Search Data Types
  export interface AutocompleteItem {
    value: string;
    label: string;
    state?: string;
    type?: 'city' | 'county' | 'state';
  }
  
  // Application State Types
  export interface AppState {
    selectedJurisdiction: string;
    activeTab: string;
    selectedPeerJurisdictions: string[];
    maxPeerJurisdictions: number;
  }
  
  // Dialog and Modal State Types
  export interface DialogState {
    isOpen: boolean;
    type?: 'add-peer' | 'share-results' | 'info';
  }
  
  // Data Loading States
  export interface DataLoadingState {
    isLoading: boolean;
    error?: string;
    lastUpdated?: Date;
  }
  
  // API Response Types (for future integration)
  export interface ApiResponse<T> {
    data: T;
    success: boolean;
    message?: string;
    timestamp: string;
  }
  
  export interface PaginatedResponse<T> extends ApiResponse<T[]> {
    pagination: {
      page: number;
      limit: number;
      total: number;
      hasMore: boolean;
    };
  }
  
  // Benchmark Data Response Types
  export interface BenchmarkData {
    jurisdiction: Jurisdiction;
    category: BenchmarkCategory;
    metrics: {
      value: string | number;
      rank: number;
      percentile: number;
      trend?: 'up' | 'down' | 'stable';
    };
    comparisons: ChartDataPoint[];
    metadata: {
      source: string;
      year: number;
      lastUpdated: Date;
    };
  }
  
  // Search and Filter Types
  export interface SearchFilters {
    query: string;
    category?: BenchmarkCategory;
    state?: string;
    populationRange?: {
      min: number;
      max: number;
    };
  }
  
  // Component Props Types
  export interface PeerJurisdictionCardProps {
    name: string;
    isAddCard?: boolean;
    onAdd?: () => void;
    onRemove?: () => void;
    isRemoving?: boolean;
  }
  
  export interface AddPeerDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onAddJurisdiction: (jurisdiction: string) => void;
    children: React.ReactNode;
  }
  
  export interface ChartCardProps extends ChartMetadata {
    data: ChartDataPoint[];
  }
  
  // Animation and UI State Types
  export interface AnimationState {
    isAnimating: boolean;
    direction?: 'in' | 'out';
    delay?: number;
  }
  
  // Form and Input Types
  export interface FormState {
    values: Record<string, any>;
    errors: Record<string, string>;
    touched: Record<string, boolean>;
    isSubmitting: boolean;
    isValid: boolean;
  }
  
  // Constants for type safety
  export const BENCHMARK_CATEGORIES: BenchmarkCategory[] = [
    'POPULATION',
    'PERMITTING',
    'HOUSING STOCK',
    'AFFORDABILITY',
    'AVAILABILITY'
  ];
  
  export const TAB_IDS = {
    PEER_COMPARISON: 'peer-comparison',
    JURISDICTION_SNAPSHOT: 'jurisdiction-snapshot',
    JURISDICTION_OVERTIME: 'jurisdiction-overtime'
  } as const;
  
  export type TabId = typeof TAB_IDS[keyof typeof TAB_IDS];
  
  // Utility types for data transformation
  export type DataTransformer<T, U> = (data: T) => U;
  export type DataValidator<T> = (data: unknown) => data is T;
  export type DataNormalizer<T> = (data: T) => T;
  